import { useQueries } from '@tanstack/react-query';
import { getGroupMembers } from '../../../../api/rbac-client/rbac-client';
import type { GroupMember, PermissionGroup } from '../../../../api/rbac-client/rbac-client.type';
import type { TableColumn } from '../table';
import { Table } from '../table';
import { EmptyStateText } from './groups-tab.styles';

/**
 * Membership state keyed by group_type, with a Set of member emails.
 */
type MembershipState = Record<string, Set<string>>;

/**
 * Builds the initial membership state from groups and their members.
 * Used by EditGroupsModal to track which users belong to which groups.
 *
 * @param {PermissionGroup[]} groups - The list of permission groups
 * @param {Record<string, GroupMember[]>} membersByGroupId - Members keyed by group ID
 * @returns {MembershipState} A record of group_type to Set of member emails
 */
function buildInitialState(
  groups: PermissionGroup[],
  membersByGroupId: Record<string, GroupMember[]>,
): MembershipState {
  const state: MembershipState = {};
  for (const group of groups) {
    const members = membersByGroupId[group.id] ?? [];
    state[group.group_type] = new Set(members.map((m) => m.email));
  }
  return state;
}

/**
 * Column definitions for the members table.
 */
const MEMBER_COLUMNS: TableColumn<GroupMember>[] = [
  { key: 'name', label: 'Username', sortable: true, width: '30%' },
  { key: 'email', label: 'Email', sortable: true, width: '70%' },
];

/**
 * Hook that fetches members for all permission groups in parallel using useQueries.
 * Uses the combine option for structurally-shared, referentially-stable results.
 *
 * @param {string[]} groupIds - Array of permission group IDs to fetch members for
 * @returns {{ membersByGroupId: Record<string, GroupMember[]>, isLoading: boolean }} Members keyed by group ID and loading state
 */
function useAllGroupMembers(groupIds: string[]): {
  membersByGroupId: Record<string, GroupMember[]>;
  isLoading: boolean;
} {
  return useQueries({
    queries: groupIds.map((id) => ({
      queryKey: ['group-members', id],
      queryFn: () => getGroupMembers(id),
      enabled: !!id,
    })),
    combine: (results) => ({
      membersByGroupId: Object.fromEntries(groupIds.map((id, i) => [id, results[i]?.data?.results ?? []])) as Record<
        string,
        GroupMember[]
      >,
      isLoading: results.some((r) => r.isLoading),
    }),
  });
}

const ALL_GROUPS_ID = 'all-groups';

interface GroupSidebarItem {
  id: string;
  name: string;
}

/**
 * Converts permission groups into sidebar items with an "All Groups" entry at the top.
 *
 * @param {PermissionGroup[]} groups - The list of permission groups
 * @returns {GroupSidebarItem[]} Array of sidebar items with id and name
 */
function toSidebarItems(groups: PermissionGroup[]): GroupSidebarItem[] {
  return [
    { id: ALL_GROUPS_ID, name: 'All Groups' },
    ...groups.map((group) => ({
      id: group.id,
      name: group.group_type_display,
    })),
  ];
}

/**
 * Renders a table of group members, or an empty state message if no members exist.
 *
 * @param {{ members: GroupMember[] }} props - The group members to display
 * @returns {JSX.Element} A table or empty state message
 */
function MembersTable({ members }: { members: GroupMember[] }) {
  if (members.length === 0) {
    return <EmptyStateText>No members in this group</EmptyStateText>;
  }

  return <Table data={members} columns={MEMBER_COLUMNS} getRowId={(row) => row.id} />;
}

export { ALL_GROUPS_ID, buildInitialState, MembersTable, toSidebarItems, useAllGroupMembers };
export type { MembershipState };
